import { useState, useEffect, useMemo } from 'react';
import { logger } from '@/features/help/utils/logger';
import { signerApi } from '@/features/application/api/signer.api';
import { Signer } from '../types/signers';
import { validateEmail } from '../utils/validation.utils';

interface UseSignersModalProps {
  isOpen: boolean;
  userShortId: string;
  mappingShortId: string;
  crmId: string | null;
  initialSigners?: Signer[];
  hasPrefetchedSigners?: boolean;
}

// Purpose: Manage local additional owner emails and trigger CRM-based invitations
export const useSignersModal = ({
  isOpen,
  userShortId,
  mappingShortId,
  crmId,
  initialSigners,
  hasPrefetchedSigners,
}: UseSignersModalProps) => {
  const [localSigners, setLocalSigners] = useState<Signer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSigners, setIsFetchingSigners] = useState(false);

  // Purpose: Build a new empty signer row for local-only entries
  const buildEmptySigner = (signerIndex: number) => {
    return {
      email: '',
      signer_index: signerIndex,
      signer_id: `local-${Date.now()}`,
      status: 'pending_invite',
      role: 'Signer',
    };
  };

  // Purpose: Determine whether a signer row is local-only
  const isLocalSigner = (signerId: string) => {
    return signerId.startsWith('local-');
  };

  // Initialize local signers when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Purpose: Load existing additional owners for the modal
    const loadExistingSigners = async () => {
      if (hasPrefetchedSigners) {
        logger.info('useSignersModal', 'Using prefetched additional owners for modal', {
          prefetchedCount: initialSigners?.length ?? 0,
        });
        setLocalSigners(
          initialSigners && initialSigners.length > 0 ? initialSigners : [buildEmptySigner(2)]
        );
        return;
      }

      if (!userShortId || !mappingShortId || !crmId) {
        logger.warn('useSignersModal', 'Missing identifiers for signer fetch', {
          hasUserShortId: !!userShortId,
          hasMappingShortId: !!mappingShortId,
          hasCrmId: !!crmId,
        });
        setLocalSigners((prev) => (prev.length > 0 ? prev : [buildEmptySigner(2)]));
        return;
      }

      setIsFetchingSigners(true);
      logger.info('useSignersModal', 'Fetching additional owners for modal', {
        userShortId,
        mappingShortId,
        crmId,
      });

      try {
        const signers = await signerApi.getSigners(userShortId, mappingShortId, crmId);
        const additionalSigners = signers.filter(
          (signer) => signer.signer_index > 1 && signer.status !== 'deactivated'
        );

        logger.info('useSignersModal', 'Fetched additional owners for modal', {
          totalCount: signers.length,
          additionalCount: additionalSigners.length,
        });

        setLocalSigners(
          additionalSigners.length > 0 ? additionalSigners : [buildEmptySigner(2)]
        );
      } catch (error) {
        logger.error('useSignersModal', 'Failed to fetch additional owners for modal', {
          error,
          userShortId,
          mappingShortId,
          crmId,
        });
        setLocalSigners((prev) => (prev.length > 0 ? prev : [buildEmptySigner(2)]));
      } finally {
        setIsFetchingSigners(false);
      }
    };

    loadExistingSigners();
  }, [isOpen, userShortId, mappingShortId, crmId, initialSigners, hasPrefetchedSigners]);

  // Purpose: Add another additional owner email input (max 2)
  const addSigner = () => {
    setLocalSigners((prev) => {
      if (prev.length >= 2) {
        logger.warn('useSignersModal', 'Attempted to add more than 2 additional owners', {
          currentCount: prev.length,
        });
        return prev;
      }

      const nextIndex = 2 + prev.length; // 2 for first additional owner, 3 for second

      logger.info('useSignersModal', 'Adding additional owner row', {
        nextIndex,
        currentCount: prev.length,
      });

      return [
        ...prev,
        buildEmptySigner(nextIndex),
      ];
    });
  };

  // Purpose: Remove an additional owner row from the modal state
  const removeSigner = (signerId: string) => {
    logger.info('useSignersModal', 'Removing additional owner row from modal state', {
      signerId,
    });
    setLocalSigners((prev) => prev.filter((s) => s.signer_id !== signerId));
  };

  // Purpose: Update the email for a specific additional owner
  const updateSignerEmail = (signerId: string, email: string) => {
    setLocalSigners((prev) => prev.map((s) => (s.signer_id === signerId ? { ...s, email } : s)));
  };

  // Purpose: Determine if submission is allowed (all emails valid)
  const isSubmissionAllowed = useMemo(() => {
    const pendingSigners = localSigners.filter((signer) => isLocalSigner(signer.signer_id));
    return (
      pendingSigners.length > 0 && pendingSigners.every((s) => s.email && validateEmail(s.email))
    );
  }, [localSigners]);

  // Purpose: Normalize and capture emails intended for submission
  const submissionEmails = useMemo(() => {
    return localSigners
      .filter((signer) => isLocalSigner(signer.signer_id))
      .map((s) => (s.email || '').trim())
      .filter((email) => email.length > 0);
  }, [localSigners]);

  // Purpose: Submit additional owner invitations using CRM-based endpoint
  const submitSigners = async () => {
    // Only require crmId (the CRM record ID) - recordId is deprecated in favor of crmId
    if (!userShortId || !mappingShortId || !crmId) {
      logger.warn('useSignersModal', 'Missing identifiers for additional owner invitation', {
        hasUserShortId: !!userShortId,
        hasMappingShortId: !!mappingShortId,
        hasCrmId: !!crmId,
      });
      return false;
    }

    if (submissionEmails.length === 0) {
      logger.warn('useSignersModal', 'No emails provided for additional owner invitation');
      return false;
    }

    setIsLoading(true);
    try {
      logger.info('useSignersModal', 'Submitting additional owner invitations', {
        emails: submissionEmails,
        userShortId,
        mappingShortId,
        crmId,
      });

      const result = await signerApi.notifyAdditionalSigners(
        userShortId,
        mappingShortId,
        crmId,
        submissionEmails
      );

      logger.info('useSignersModal', 'Additional owner invitations initiated', {
        emails: submissionEmails,
        debugId: result?.debugId,
      });

      return true;
    } catch (error) {
      logger.error('useSignersModal', 'Failed to submit additional owner invitations', {
        error,
      });
      throw error; // Re-throw to let component handle
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signers: localSigners,
    addSigner,
    removeSigner,
    updateSignerEmail,
    isSubmissionAllowed,
    submitSigners,
    isLoading,
    isFetchingSigners,
    submissionEmails,
  };
};
